import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, TableSortable, Typography } from '@saviynt/design-system';

import { SodMessage } from '../../../../shared/components';
import { CF_SEC_BLD } from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';
import type { RootState } from '../../../../shared/types';
import {
  ACTION_COLUMN,
  COLUMN_WIDTH,
  EXCLUDED_KEY,
  PINNED_COLUMN,
  RISK_DETAILS_PRIORITY_COLUMNS,
  SORT_OPTIONS,
  USER_DETAILS_PRIORITY_COLUMNS,
} from '../../constants';
import { type ReportColumnTypes } from '../../data-store/entReportsSlice';
import { type EntReportTableProps, type SodEntReportData } from '../../types';
import ExportBanner from '../EntReportBanner/ExportBanner';

import './EntReportTable.css';

/**
 * EntReportTable Component
 *
 * Advanced sortable table component for displaying entitlement report data with dynamic column
 * configuration, priority-based column ordering, and export functionality. Supports both risk
 * and user detail report types with virtualization for optimal performance.
 *
 * @component
 * @returns {React.ReactElement} Sortable table with features including:
 *   - Dynamic column generation from data or Redux state
 *   - Priority-based column ordering for risk/user reports
 *   - Column configuration with drag-and-drop reordering
 *   - Virtualization for large datasets
 *   - Export configuration callback support
 *   - Responsive design with sticky headers
 *
 * @example
 * <EntReportTable onColumnConfigChange={handleColumnConfig} />
 */
function EntReportTable({
  onColumnConfigChange,
}: EntReportTableProps): ReactElement {
  const l = useInvertedLocalize();

  const localizedSortOptions = useMemo(
    () =>
      SORT_OPTIONS.map((opt) => ({
        ...opt,
        label: l(opt.label),
      })),
    [l]
  );

  const createColumnConfig = useCallback(
    (
      column: ReportColumnTypes,
      index: number,
      priorityColumns?: typeof RISK_DETAILS_PRIORITY_COLUMNS,
      isPriorityColumn = false
    ) => {
      const priorityColumn = priorityColumns?.find(
        (p) => p.value === column.value
      );
      const displayLabel = priorityColumn
        ? l(priorityColumn.label)
        : column.label;

      return {
        key: column.value,
        label: displayLabel,
        value: column.value,
        isSortable: true,
        isChecked: isPriorityColumn,
        isDisabled: false,
        isPinned: column.value === PINNED_COLUMN || index === 0,
        width: COLUMN_WIDTH,
        isColumnConfigEnabled: true,
      };
    },
    [l]
  );

  const createGeneratedColumn = useCallback(
    (
      key: string,
      index: number,
      priorityColumn?: { label: string; value: string },
      isPriorityColumn = false
    ) => ({
      key,
      label: priorityColumn ? l(priorityColumn.label) : formatLabel(key),
      value: key,
      isSortable: true,
      isChecked: isPriorityColumn,
      isDisabled: false,
      isPinned: key === PINNED_COLUMN || index === 0,
      width: COLUMN_WIDTH,
      isColumnConfigEnabled: true,
    }),
    [l]
  );

  const [columns, setColumns] = useState<
    {
      key: string;
      label: string;
      value: string;
      isSortable: boolean;
      isChecked: boolean;
      isPinned: boolean;
      isDisabled: boolean;
      width: string;
      isColumnConfigEnabled?: boolean;
    }[]
  >([]);

  const [row, setRow] = useState<SodEntReportData[]>([]);
  const isUpdatingFromActiveColumns = useRef(false);

  const { activeColumns, reportData, selectedReport } = useTypedSelector(
    (state: RootState) => ({
      activeColumns: state.aag.entReports.activeColumns,
      reportData: state.aag.entReports.reportData,
      selectedReport: state.aag.entReports.selectedReport,
    })
  );

  // Helper function to generate column configuration for export
  const generateColumnConfig = useCallback(
    (columnsToProcess: typeof columns) => {
      if (columnsToProcess.length > 0 && onColumnConfigChange) {
        const validColumns = columnsToProcess.filter(
          (col) => col.isChecked && col.key !== 'actionColumn'
        );

        const selectedColumns = validColumns.map((col) => col.value);
        const columnOrder = validColumns.reduce<Record<string, number>>(
          (order, col, index) => {
            order[col.value] = index + 1;

            return order;
          },
          {}
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        onColumnConfigChange({ selectedColumns, columnOrder });
      }
    },
    [onColumnConfigChange]
  );

  // Handle activeColumns changes from Redux state or generate from data
  useEffect(() => {
    if (activeColumns.length > 0) {
      isUpdatingFromActiveColumns.current = true;

      let priorityColumns: typeof RISK_DETAILS_PRIORITY_COLUMNS | undefined;

      if (selectedReport === 'INHERENT_ENTITLEMENT_RISK') {
        priorityColumns = RISK_DETAILS_PRIORITY_COLUMNS;
      } else if (selectedReport === 'INHERENT_ENTITLEMENT_USER') {
        priorityColumns = USER_DETAILS_PRIORITY_COLUMNS;
      }

      const sortedColumns = priorityColumns
        ? sortColumnsByPriority(activeColumns, priorityColumns)
        : activeColumns;

      const modifiedColumns = sortedColumns.map((column, index) => {
        const isPriorityColumn = priorityColumns?.some(
          (p) => p.value === column.value
        );

        return createColumnConfig(
          column,
          index,
          priorityColumns,
          isPriorityColumn
        );
      });

      modifiedColumns.push(ACTION_COLUMN);

      setColumns(modifiedColumns);
      generateColumnConfig(modifiedColumns);

      setTimeout(() => {
        isUpdatingFromActiveColumns.current = false;
      }, 0);
    } else if (Array.isArray(reportData) && reportData.length > 0) {
      // Generate columns from data if no activeColumns
      const firstItem = reportData[0] as Record<string, unknown>;
      let generatedColumns: {
        key: string;
        label: string;
        value: string;
        isSortable: boolean;
        isChecked: boolean;
        isPinned: boolean;
        isDisabled: boolean;
        width: string;
        isColumnConfigEnabled?: boolean;
      }[];

      if (selectedReport === 'INHERENT_ENTITLEMENT_RISK') {
        const availableKeys = Object.keys(firstItem).filter(
          (key) => key !== EXCLUDED_KEY
        );
        const sortedKeys = processKeysWithPriority(
          availableKeys,
          RISK_DETAILS_PRIORITY_COLUMNS
        );

        generatedColumns = sortedKeys.map((key, index) => {
          const priorityColumn = RISK_DETAILS_PRIORITY_COLUMNS.find(
            (p) => p.value === key
          );
          const isPriorityColumn = RISK_DETAILS_PRIORITY_COLUMNS.some(
            (p) => p.value === key
          );

          return createGeneratedColumn(
            key,
            index,
            priorityColumn,
            isPriorityColumn
          );
        });
      } else if (selectedReport === 'INHERENT_ENTITLEMENT_USER') {
        const availableKeys = Object.keys(firstItem).filter(
          (key) => key !== EXCLUDED_KEY
        );
        const sortedKeys = processKeysWithPriority(
          availableKeys,
          USER_DETAILS_PRIORITY_COLUMNS
        );

        generatedColumns = sortedKeys.map((key, index) => {
          const priorityColumn = USER_DETAILS_PRIORITY_COLUMNS.find(
            (p) => p.value === key
          );
          const isPriorityColumn = USER_DETAILS_PRIORITY_COLUMNS.some(
            (p) => p.value === key
          );

          return createGeneratedColumn(
            key,
            index,
            priorityColumn,
            isPriorityColumn
          );
        });
      } else {
        generatedColumns = [];
      }

      generatedColumns.push(ACTION_COLUMN);

      setColumns(generatedColumns);
      generateColumnConfig(generatedColumns);
    } else {
      // Reset columns if no data
      setColumns([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeColumns,
    reportData,
    selectedReport,
    createColumnConfig,
    createGeneratedColumn,
  ]);

  // Handle column configuration changes when user modifies columns
  useEffect(() => {
    if (columns.length > 0 && !isUpdatingFromActiveColumns.current) {
      generateColumnConfig(columns);
    }
  }, [columns, generateColumnConfig]);

  useEffect(() => {
    if (Array.isArray(reportData)) {
      if (reportData.length > 0) {
        const modifiedReportData = reportData.map((item): SodEntReportData => {
          const result: SodEntReportData = {};

          Object.entries(item as Record<string, unknown>).forEach(
            ([key, value]: [string, unknown]) => {
              // Convert array values to string with newlines
              let displayValue: string;

              if (Array.isArray(value)) {
                displayValue = value.join('\n');
              } else if (value != null) {
                displayValue = String(value);
              } else {
                displayValue = '';
              }

              (result as Record<string, React.ReactElement>)[key] = (
                <Typography kind='body2' color={CF_SEC_BLD} lineLimit={2}>
                  {displayValue}
                </Typography>
              );
            }
          );

          return result;
        });

        setRow(modifiedReportData);
      } else {
        setRow([]);
      }
    }
  }, [reportData]);

  if (typeof reportData === 'string') {
    return (
      <Box className='Sod-Report-noRecordFound'>
        <SodMessage
          isSearchActive={false}
          isFilterActive={false}
          handleFilterClearBtn={() => {}}
          defaultTitle={l('No Records')}
          defaultMessage={l(
            'No Record Found. Change some filters and try again.'
          )}
        />
      </Box>
    );
  }

  const sortOptionsConfig = columns
    .filter((item) => item.key && item.isSortable)
    .map((item) => ({ key: item.key, sortOptions: localizedSortOptions }));

  return (
    <Box className='Sod-Ent-Report-table ReportTable'>
      <ExportBanner className='Sod-Ent-ReportTable-exportBanner' />
      <Typography
        className='Sod-Ent-Report-table-items'
        kind='body2'
        desktop='body2-d'
        mobile='body2-d'>
        {row.length} {row.length === 1 ? l('Item') : l('Items')}
      </Typography>
      <TableSortable
        data={row}
        columns={columns}
        setColumns={setColumns}
        sortConfigs={sortOptionsConfig}
        isWindowScroll={false}
        isVirtualizationEnabled
        tableHeight='calc(100vh - 21rem)'
        columnConfigs={{
          isDndDisabled: false,
          isHeaderSticky: true,
          isHorizontalScroll: true,
        }}
      />
    </Box>
  );
}

// Helper functions
const formatLabel = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

const processKeysWithPriority = (
  availableKeys: string[],
  priorityColumns: typeof RISK_DETAILS_PRIORITY_COLUMNS
) => {
  const priorityKeys = priorityColumns
    .filter((p) => availableKeys.includes(p.value))
    .map((p) => p.value);
  const otherKeys = availableKeys.filter((key) => !priorityKeys.includes(key));

  return [...priorityKeys, ...otherKeys];
};

const sortColumnsByPriority = (
  columns: ReportColumnTypes[],
  priorityColumns: typeof RISK_DETAILS_PRIORITY_COLUMNS
) => {
  const priority: ReportColumnTypes[] = [];
  const other: ReportColumnTypes[] = [];

  columns.forEach((column) => {
    const priorityColumn = priorityColumns.find(
      (p) => p.value === column.value
    );

    if (priorityColumn) {
      priority.push({ ...column, label: priorityColumn.label });
    } else {
      other.push(column);
    }
  });

  priority.sort((a, b) => {
    const aIndex = priorityColumns.findIndex((p) => p.value === a.value);
    const bIndex = priorityColumns.findIndex((p) => p.value === b.value);

    return aIndex - bIndex;
  });

  return [...priority, ...other];
};

EntReportTable.defaultProps = {
  onColumnConfigChange: undefined,
};

export default memo(EntReportTable);
